// Staff person-to-address map (kanban ba007a0f). Backend:
// app/routes/staff_addresses.py (/api/admin/staff-addresses).
//
// Scott, review c63b8aa2: "Check our emails to figure out what their actual
// addresses are ... sometimes there might be mappings and multiples." The map
// is rebuilt nightly from our mail; a person confirms or rejects what the
// builder suggests; the hand-off curator trusts confirmed rows only.
import api from './api';

export type StaffAddressStatus = 'unmapped' | 'suggested' | 'confirmed' | 'rejected' | 'shared';

export interface StaffAddress {
  id: string;
  address: string;
  person_id: string | null;
  status: StaffAddressStatus;
  source: string;
  display_names: Record<string, number>;
  from_count: number;
  to_count: number;
  cc_count: number;
  sent_count: number;
  first_seen: string | null;
  last_seen: string | null;
  suggested_reason: string | null;
  decided_by: string | null;
  decided_at: string | null;
  history: Array<Record<string, unknown>>;
}

export interface StaffPerson {
  id: string;
  name: string;
  portal_user_id: string | null;
  portal_email: string | null;
  sage_salesperson_no: string | null;
  addresses: StaffAddress[];
  rejected: StaffAddress[];
}

export interface StaffAddressMap {
  people: StaffPerson[];
  suggestions: StaffAddress[];
  shared: StaffAddress[];
  unmapped: StaffAddress[];
  counts: Record<string, number>;
}

export type Decision = 'confirmed' | 'rejected' | 'shared' | 'unmapped';

const BASE = '/admin/staff-addresses';

export const staffAddressesApi = {
  async map(): Promise<StaffAddressMap> {
    const r = await api.get<StaffAddressMap>(BASE);
    return r.data;
  },
  async decide(
    addressId: string,
    status: Decision,
    opts: { personId?: string; reason?: string } = {},
  ): Promise<StaffAddress> {
    const r = await api.post<StaffAddress>(`${BASE}/addresses/${addressId}/decision`, {
      status,
      person_id: opts.personId ?? null,
      reason: opts.reason ?? '',
    });
    return r.data;
  },
  async declarePerson(body: {
    name: string;
    sage_salesperson_no?: string | null;
  }): Promise<StaffPerson> {
    const r = await api.post<StaffPerson>(`${BASE}/people`, body);
    return r.data;
  },
  async rebuild(): Promise<Record<string, number>> {
    const r = await api.post<{ counts: Record<string, number> }>(`${BASE}/rebuild`);
    return r.data.counts;
  },
};

/** Messages that touched the address in the builder's window. */
export function trafficOf(a: Pick<StaffAddress, 'from_count' | 'to_count' | 'cc_count' | 'sent_count'>): number {
  return a.from_count + a.to_count + a.cc_count + a.sent_count;
}

/** Busiest first; ties by address. */
export function sortByTraffic<T extends StaffAddress>(rows: T[]): T[] {
  return [...rows].sort((x, y) => trafficOf(y) - trafficOf(x) || x.address.localeCompare(y.address));
}

/** "Roblyn Jericho (619), Jericho Roblyn (4)" — the names the mail used. */
export function namesSeen(a: Pick<StaffAddress, 'display_names'>, limit = 3): string {
  return Object.entries(a.display_names)
    .sort((x, y) => y[1] - x[1])
    .slice(0, limit)
    .map(([name, n]) => `${name} (${n})`)
    .join(', ');
}

/** The person a suggestion points at, by id. */
export function personNamed(people: StaffPerson[], personId: string | null): string {
  if (!personId) return '';
  return people.find((p) => p.id === personId)?.name ?? '';
}

/** "116 sent · 2,425 received · 137 cc" — zero parts left out. */
export function trafficSummary(a: Pick<StaffAddress, 'from_count' | 'to_count' | 'cc_count' | 'sent_count'>): string {
  const parts: string[] = [];
  if (a.from_count) parts.push(`${a.from_count.toLocaleString()} sent by them`);
  if (a.to_count) parts.push(`${a.to_count.toLocaleString()} received`);
  if (a.cc_count) parts.push(`${a.cc_count.toLocaleString()} cc`);
  if (a.sent_count) parts.push(`${a.sent_count.toLocaleString()} from Merlin`);
  return parts.join(' · ') || 'no mail in the window';
}
